<script setup lang="ts">
import type { Wallet } from '@solana/wallet-adapter-vue'
import { WalletReadyState } from '@solana/wallet-adapter-base'
import { useWallet } from '@solana/wallet-adapter-vue'
import { Screen } from 'quasar'
import { PhantomWalletName, SolflareWalletName } from 'wallets'

import { OKXWalletName } from 'wallets/adapters/OkxWalletAdapter'
import cloverSvg from '~/assets/img/wallets/clover.svg'
import ledgerDarkSvg from '~/assets/img/wallets/ledger.svg'
import mathWalletDarkSvg from '~/assets/img/wallets/mathwallet.svg'
import AppInputBasic from '~/components/Common/AppInputBasic.vue'
import { useMobileDetect } from '~/hooks/mobile'
import { copy, shortenAddress } from '~/utils'

type WalletSettings = {
  priority: Record<string, number>
  icons: Record<string, string>
  darkIcons: Record<string, string>
  deepLinks: Record<string, string>
}

const recommended = new Set(['Solflare', 'Phantom'])

const settings: WalletSettings = {
  priority: {
    solflare: 10,
    phantom: 20,
    sollet: 5,
    nufi: 5,
  },
  icons: {
    clover: cloverSvg,
  },
  darkIcons: {
    ledger: ledgerDarkSvg,
    mathwallet: mathWalletDarkSvg,
  },
  deepLinks: {
    phantom: 'https://phantom.app/ul/browse/{uri}?ref={ref}',
    solflare: 'solflare://ul/v1/browse/{uri}?ref={ref}',
    okxwallet: 'okx://wallet/dapp/details?dappUrl={host}',
  },
}

const { userAgent, isWebView, isMobileOs } = useMobileDetect()
const isMobile = computed(() => isMobileOs.value && !isWebView.value)

function isActiveWallet(w: Wallet) {
  return w.readyState === WalletReadyState.Installed || w.readyState === WalletReadyState.Loadable
}

function isMobileRelevant(w: ExtendedWallet) {
  return isMobile.value && (w.deepLink || w.adapter.name === 'WalletConnect')
}

const wallet = useWallet()
const { connected, connecting } = wallet

const searchWallet = ref('')

const dialog = ref(false)
const popover = ref(false)
const connectingTimer = ref<any>(0)

function cancelTimer() {
  if (connectingTimer.value > 0) {
    clearTimeout(connectingTimer.value)
  }
}

async function select(w: Wallet) {
  cancelTimer()
  await wallet.select(w.adapter.name)
  dialog.value = false
  connectingTimer.value = setTimeout(() => {
    connecting.value = false
  }, 10_000)
  await wallet.connect()
}

const webViewWallet = ref()
if (isWebView.value) {
  const ua = String(userAgent.value).toLowerCase()
  if (ua.includes('phantom')) {
    webViewWallet.value = PhantomWalletName
    // @ts-expect-error...
  } else if (globalThis.solflare) {
    webViewWallet.value = SolflareWalletName
  } else if (/okapp/i.test(ua)) {
    webViewWallet.value = OKXWalletName
  }
}

type ExtendedWallet = Wallet & { icon: string, darkIcon: string, deepLink: string }
const walletAddress = computed(() => wallet.publicKey.value?.toBase58() ?? '')
const walletShortAddress = computed(() => shortenAddress(walletAddress.value, 5))
const wallets = computed<ExtendedWallet[]>(() =>
  [...wallet.wallets.value as ExtendedWallet[]]
    .map((w) => {
      const key = w.adapter.name.toLowerCase().replaceAll(/\s/g, '')
      // only show deep links on mobile
      if (isMobile.value && settings.deepLinks[key]) {
        w.deepLink = settings.deepLinks[key]!
          .replace('{uri}', encodeURIComponent(`${location.href}?wallet=${w.adapter.name}`))
          .replace('{ref}', encodeURIComponent(location.origin))
          .replace('{host}', location.host)
      }
      return w
    })
    .sort((a, b) => {
      const aPriority = settings.priority[a.adapter.name.toLowerCase()] ?? 1
      const bPriority = settings.priority[b.adapter.name.toLowerCase()] ?? 1
      return (
        bPriority
        - aPriority
        + ((isActiveWallet(b) ? 1 : 0) - (isActiveWallet(a) ? 1 : 0))
      )
    }),
)

const computedWallets = computed(() => {
  const filteredWallets = wallets.value.filter(w => w.adapter.name.toLowerCase().includes(searchWallet.value.toLowerCase()))
  return filteredWallets.reduce((acc: { installed: ExtendedWallet[], others: ExtendedWallet[] }, w) => {
    const isWebViewMatch = !webViewWallet.value || w.adapter.name === webViewWallet.value

    if (w.adapter.name === 'WalletConnect') {
      acc.installed.push(w)
      return acc
    }
    const isInstalled = recommended.has(w.adapter.name)
      || w.readyState === WalletReadyState.Installed
      || (isMobile.value && w.deepLink)

    if (isWebViewMatch && (isMobile.value ? isMobileRelevant(w) : true)) {
      if (isInstalled) {
        acc.installed.push(w)
      } else {
        acc.others.push(w)
      }
    }
    return acc
  }, { installed: [], others: [] })
})

const btnSizeByScreen = computed(() => Screen.width < 1024 ? 'md' : 'lg')

watch(connected, () => {
  if (connected.value) {
    cancelTimer()
  }
})

function disconnect() {
  dialog.value = false
  wallet.disconnect()
}

if (webViewWallet.value) {
  wallet.select(webViewWallet.value)
  wallet.connect()
}

function show() {
  dialog.value = true
}
</script>

<template>
  <app-btn
    v-if="connected"
    class="connect-btn"
    variant="outline"
    :size="btnSizeByScreen"
  >
    <span>{{ walletShortAddress }}</span>

    <app-popover
      v-model="popover"
      class="connect-menu"
      anchor="top middle"
      self="bottom middle"
      :transition-duration="100"
      :offset="[0, 6]"
    >
      <div class="wallet-btn-menu">
        <div v-close-popup class="wallet-btn-menu__item" @click="copy(walletAddress)">
          <i-app-copy />
          Copy wallet address
        </div>
        <div v-close-popup class="wallet-btn-menu__item" @click="disconnect">
          <i-app-disconnect />
          Disconnect wallet
        </div>
      </div>
    </app-popover>
  </app-btn>
  <app-btn
    v-else
    class="connect-btn primary-connect-btn"
    variant="outline"
    :loading="connecting"
    :size="btnSizeByScreen"
    rounded
    @click="show"
  >
    <span>Connect wallet</span>
  </app-btn>

  <app-dialog v-model="dialog" class="wallets-list-dialog">
    <q-card v-if="!connected" class="wallet-connect-card">
      <q-card-section>
        <div class="wallet-connect-card__title">
          Connect your wallet
        </div>
      </q-card-section>
      <q-card-section class="wallet-connect-card__search">
        <app-input-basic v-model="searchWallet" size="md" placeholder="Filter wallets by name" />
      </q-card-section>
      <q-card-section class="scroll">
        <div
          v-if="computedWallets.installed.length > 0 || computedWallets.others.length > 0"
          class="wallets-list-container"
        >
          <div class="wallets-list-container__title">
            Recommended wallets
          </div>
          <div class="wallets-list">
            <app-btn
              v-for="w in computedWallets.installed"
              :key="`wallet-${w.adapter.name}`"
              class="connect-btn"
              variant="outline"
              unelevated @click="select(w)"
            >
              <span class="wallet-btn__content">
                <img class="wallet-icon" :src="w.adapter.icon" :alt="w.adapter.name">
                {{ w.adapter.name }}
              </span>
            </app-btn>
          </div>
          <app-expansion v-if="computedWallets.others.length > 0" title="More wallets" class="wallets-list-wrapper">
            <div class="wallets-list">
              <app-btn
                v-for="w in computedWallets.others"
                :key="`wallet-${w.adapter.name}`"
                class="connect-btn"
                variant="outline"
                unelevated @click="select(w)"
              >
                <span class="wallet-btn__content">
                  <img class="wallet-icon" :src="w.adapter.icon" :alt="w.adapter.name">
                  {{ w.adapter.name }}
                </span>
              </app-btn>
            </div>
          </app-expansion>
        </div>
        <div v-else class="wallets-list-container no-wallets text-sm">
          <i-app-wallet-off class="wallet-icon" />
          No Matches Found
          <span class="desc">
            Looks like we couldn’t find any wallet matching your query. Please check your spelling and try again.
          </span>

          <app-btn class="reset-btn" size="sm" data-variant="secondary" @click="searchWallet = ''">
            Reset
          </app-btn>
        </div>
      </q-card-section>
    </q-card>
  </app-dialog>
</template>

<style lang="scss">
.connect-wallet-btn {
  @media (max-width: $breakpoint-xs) {
    width: 40px;
    height: 40px;
    padding: 0 !important;

    svg {
      width: 20px;
      height: 20px;
    }
  }
}

.connect-btn {
  min-height: 44px;
  width: 168px;
  word-break: unset;

  @media (max-width: $breakpoint-xs) {
    .q-btn__content {
      font-size: 11px;
    }
    width: 110px;
    min-height: 32px;
  }
}

.wallets-list-dialog {
  .q-dialog__body {
    background-color: $base-white;
  }

  .q-card {
    padding: 0 !important;
    display: flex;
    flex-direction: column;
    height: 100%;

    .q-card__section:has(.wallets-list-container) {
      height: 100%;
      max-height: 70dvh;

      @media (max-width: $breakpoint-xs) {
        max-height: 100%;
      }
    }
  }
}

.wallet-connect-card {
  width: 512px;
  text-align: center;
  box-shadow: none;

  @media (max-width: $breakpoint-xs) {
    width: 100%;
  }

  &__title {
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
    letter-spacing: -0.48px;
    padding-top: $space-60;
    color: $black;

    @media (max-width: $breakpoint-xs) {
      font-size: 20px;
      padding-bottom: $space-50;
    }
  }

  &__search {
    padding: $space-xxl $space-60 !important;

    .q-field__prepend,
    .q-field__control {
      height: 100%;
    }

    .base-input {
      height: 62px;
      border-radius: $border-radius-sm;
      background: $gray-03;
      padding: 6px 17px 6px 10px;
      border: 1px solid $gray-02;
    }
  }

  .wallets-list-container {
    margin: $space-xxs 24px $space-xl 24px;
    border-radius: $border-radius-sm;
    background-color: $gray-03;
    border: 1px solid $gray-02;
    padding: $space-md;

    &__title {
      color: $black;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: -0.16px;
      text-align: left;
      margin-bottom: 20px;
    }

    .q-expansion-item {
      box-shadow: none;
      background: transparent;
      width: 100%;

      .q-icon,
      .q-item__label {
        color: $black;
      }

      &--expanded {
        background-color: transparent;
      }

      .q-item {
        padding: $space-md $space-xxs;

        &__label {
          font-size: 14px;
          font-weight: 500;
          line-height: 20px;
          letter-spacing: -0.16px;
          text-align: left;
        }
      }

      .content {
        padding: 0;

        &::before {
          display: none;
        }
      }
    }

    &.no-wallets {
      padding: 52px 64px;
      display: flex;
      flex-direction: column;
      align-items: center;
      font-weight: 500;

      .wallet-icon {
        width: 80px;
        height: 80px;
        color: $zinc-700;
        margin-bottom: $space-60;
      }

      .desc {
        font-weight: 400;
        color: $zinc-500;
        padding-top: $space-sm;
      }

      .reset-btn {
        margin-top: $space-60;
      }
    }
  }

  .wallets-list {
    display: grid;
    flex-wrap: wrap;
    width: 100%;
    border-radius: $border-radius-md;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: $breakpoint-xs) {
      flex-direction: column;
    }

    .connect-btn {
      background-color: $zinc-800;
      font-size: 14px;
      color: $black;
      width: 100%;
      height: 44px;
      padding: 0 12px;

      @media (max-width: $breakpoint-xs) {
        width: 100%;
      }

      .wallet-btn__content {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: $space-lg;

        .wallet-icon {
          width: 32px;
          height: 32px;
          object-fit: contain;

          @media (max-width: $breakpoint-xs) {
            margin-right: 0;
          }
        }
      }
    }
  }

  &__terms {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: $space-sm;
    height: 14px;

    span {
      color: $zinc-400;
    }

    a {
      color: $primary;
    }

    .links {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: $space-xl;

      span {
        color: $primary;
      }
    }
  }
}
.q-menu.connect-menu {
  background-color: $base-white;
  border-radius: $border-radius-lg;
  padding: 0;
}
.wallet-btn-menu {
  background: rgba(32, 179, 149, 0.2);

  &__item {
    width: 200px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: $space-sm;
    padding: $space-sm;
    color: $black;
    cursor: pointer;

    &:hover {
      background-color: $green;
      color: $base-white;
    }

    i {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        width: 100%;
        height: 100%;
      }
    }
  }
}
</style>
